import React from 'react';
import { Box } from '@mui/material';
import { TEmail, EmailAnalytics } from '../../types';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import InsertInvitationIcon from '@mui/icons-material/InsertInvitation';
import StatusStatistics from './StatusStatistics';
import OpenEmailStatistics from './OpenEmailStatistics';

interface AnalyticsProps {
  selectedEmail: TEmail;
  emailAnalytics: { analytics: EmailAnalytics } | undefined;
  isLoading: boolean;
}

const Analytics: React.FC<AnalyticsProps> = ({ selectedEmail, emailAnalytics, isLoading }) => {
  if (isLoading) {
    return <Box>Loading analytics...</Box>;
  }

  if (!emailAnalytics?.analytics) {
    return <Box>No analytics data available</Box>;
  }

  const analytics = emailAnalytics.analytics;

  return (
    <Box>
      <Box sx={{ display: 'flex', marginLeft: '0', width: '100%' }}>
        {/* Left Column */}
        <Box sx={{ flex: '0 0 50%', paddingRight: '25px' }}>
          {/* Recipients Widget */}
          <Box
            sx={{
              border: '1px solid #E3E3E3',
              borderRadius: '3px',
              position: 'relative',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              paddingLeft: '4%',
              height: '6.5rem'
            }}
          >
            <Box
              sx={{
                backgroundColor: '#EDF2F5',
                padding: '12px',
                borderRadius: '16px',
                '& svg': {
                  color: '#084D6D',
                  fontSize: '28px'
                }
              }}
            >
              <InsertDriveFileIcon />
            </Box>
            <Box sx={{ marginLeft: '1.75rem' }}>
              <Box
                sx={{
                  fontSize: '19px',
                  color: '#084D6D',
                  fontWeight: 'bold'
                }}
              >
                {analytics.recipients}
              </Box>
              <Box
                sx={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#333333'
                }}
              >
                Recipients
              </Box>
            </Box>
          </Box>

          {/* Date Widget */}
          <Box
            sx={{
              border: '1px solid #E3E3E3',
              borderRadius: '3px',
              position: 'relative',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              paddingLeft: '4%',
              height: '6.5rem',
              marginTop: '50px'
            }}
          >
            <Box
              sx={{
                backgroundColor: '#EDF2F5',
                padding: '12px',
                borderRadius: '16px',
                '& svg': {
                  color: '#084D6D',
                  fontSize: '28px'
                }
              }}
            >
              <InsertInvitationIcon />
            </Box>
            <Box sx={{ marginLeft: '15%', width: '100%' }}>
              <Box sx={{ display: 'flex' }}>
                <Box sx={{ flex: '0 0 50%' }}>
                  <Box
                    sx={{
                      fontSize: '16px',
                      color: '#084D6D'
                    }}
                  >
                    Created At
                  </Box>
                  <Box>
                    {analytics.created_at
                      ? new Date(analytics.created_at).toLocaleDateString()
                      : '-'}
                  </Box>
                </Box>
                <Box sx={{ flex: '0 0 50%' }}>
                  <Box
                    sx={{
                      fontSize: '16px',
                      color: '#084D6D'
                    }}
                  >
                    Delayed To
                  </Box>
                  <Box>{analytics.delayed_to || '-'}</Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Right Column */}
        <Box sx={{ flex: '0 0 50%', paddingLeft: '25px' }}>
          <Box
            sx={{
              border: '1px solid #E3E3E3',
              borderRadius: '3px',
              position: 'relative',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              paddingLeft: '4%',
              height: '100%'
            }}
          >
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', marginBottom: '3rem' }}>
                <Box sx={{ flex: '0 0 50%', margin: 'auto', padding: '0 4% 0 8%' }}>
                  <Box
                    sx={{
                      fontSize: '16px',
                      color: '#084D6D'
                    }}
                  >
                    User
                  </Box>
                  <Box>{analytics.user?.name ? analytics.user?.name : '-'}</Box>
                </Box>
                <Box sx={{ flex: '0 0 50%', margin: 'auto', padding: '0 4% 0 8%' }}>
                  <Box
                    sx={{
                      fontSize: '16px',
                      color: '#084D6D'
                    }}
                  >
                    Email Template
                  </Box>
                  <Box sx={{ overflowWrap: 'break-word', paddingRight: '15px' }}>
                    {analytics.email_template || '-'}
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: 'flex' }}>
                <Box sx={{ flex: '0 0 50%', margin: 'auto', padding: '0 4% 0 8%' }}>
                  <Box
                    sx={{
                      fontSize: '16px',
                      color: '#084D6D'
                    }}
                  >
                    Status
                  </Box>
                  <Box>{analytics.status || '-'}</Box>
                </Box>
                <Box sx={{ flex: '0 0 50%', margin: 'auto', padding: '0 4% 0 8%' }}>
                  <Box
                    sx={{
                      fontSize: '16px',
                      color: '#084D6D'
                    }}
                  >
                    From
                  </Box>
                  <Box>{analytics.from || '-'}</Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Statistics Components */}
      <Box sx={{ width: '100%', marginTop: '50px' }}>
        <StatusStatistics isLoading={isLoading} emailAnalytics={analytics} />
      </Box>
      <Box sx={{ width: '100%', marginTop: '50px' }}>
        <OpenEmailStatistics isLoading={isLoading} emailAnalytics={analytics} />
      </Box>
    </Box>
  );
};

export default Analytics;
